// GC Scouting Data modal (2026-10-16) -- admin-only, opened per game from Schedule Sync.
//
// Step 1: paste an opponent's games from GameChanger (Plays page, plus Box score page so the parse
//         can be checked against GC's own totals). Each game is saved ONCE per opponent at
//         opponents/{opponentId}/gcGames/{gameKey}, so every Warriors team that faces them later
//         reuses the same data -- the opponents registry is org-wide on purpose (see firestore.rules).
// Step 2: generate the report for this specific Warriors game. It's written to the existing
//         gcScoutingReports/{gameId} doc (merge), so coach visibility, the Coach Portal link and the
//         push-notification digest/catch-up functions all keep working unchanged.
//
// Raw pasted text is stored alongside the parsed record so games can be re-parsed if the parser
// improves (SCOUTING_PARSER_VERSION bump) without anyone re-pasting.
import { useEffect, useMemo, useState } from "react";
import { collection, deleteDoc, doc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "./firebase.js";
import { LOOKBACK_DAYS, SCOUTING_PARSER_VERSION, buildGameRecord, buildScoutingReport, gamesInWindow, guessScoutedTeam, parsePlays } from "../shared/scouting.mjs";

const clean = (o) => JSON.parse(JSON.stringify(o)); // Firestore rejects undefined

export default function ScoutingDataModal({ opponent, reportBase, existingReport, ui, showToast, onClose, onGenerated }) {
  const { C, btnGhost, btnPrimary, inputStyle } = ui;
  const [games, setGames] = useState(null);
  const [playsText, setPlaysText] = useState("");
  const [boxText, setBoxText] = useState("");
  const [teamPick, setTeamPick] = useState("");
  const [busy, setBusy] = useState(null); // "save" | "gen" | gameKey being deleted
  const [confirmDel, setConfirmDel] = useState(null);
  const gcName = opponent.gcTeamName || opponent.name;
  const gamesRef = collection(db, "opponents", opponent.id, "gcGames");
  // Only opponent games from the LOOKBACK_DAYS before this Warriors game go into the report.
  const asOf = reportBase.gameDate ? new Date(reportBase.gameDate) : new Date();
  const win = useMemo(() => gamesInWindow(games || [], { asOf }), [games, reportBase.gameDate]); // eslint-disable-line react-hooks/exhaustive-deps
  const outside = new Set(win.excluded.map((g) => g.id));

  const load = async () => {
    const snap = await getDocs(gamesRef);
    setGames(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.date || "").localeCompare(a.date || "")));
  };
  useEffect(() => { load().catch((e) => { showToast("Error: " + (e.message || "Could not load games")); setGames([]); }); }, [opponent.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live parse of whatever's pasted -- cheap (a few ms) and gives instant feedback.
  const header = useMemo(() => (playsText.trim() ? parsePlays(playsText) : null), [playsText]);
  const teamNames = header ? [header.away, header.home].filter(Boolean) : [];
  const guessed = header ? guessScoutedTeam(teamNames, gcName) : null;
  const scouted = teamPick && teamNames.includes(teamPick) ? teamPick : guessed;
  const preview = useMemo(() => {
    if (!header || !scouted) return null;
    try { return { rec: buildGameRecord({ playsText, boxText, scoutedTeam: scouted }) }; } catch (e) { return { error: e.message }; }
  }, [header, scouted, playsText, boxText]);

  const saveGame = async () => {
    if (!preview?.rec) return;
    setBusy("save");
    try {
      const rec = preview.rec;
      const already = games?.some((g) => g.id === rec.key);
      await setDoc(doc(gamesRef, rec.key), clean({ ...rec, playsText, boxText, ingestedBy: auth.currentUser?.email || "", ingestedAt: new Date().toISOString() }));
      // Remember the exact GC spelling of this opponent so the next paste auto-selects it.
      if (scouted !== opponent.gcTeamName) await updateDoc(doc(db, "opponents", opponent.id), { gcTeamName: scouted });
      setPlaysText(""); setBoxText(""); setTeamPick("");
      await load();
      showToast(already ? "✓ Game updated" : `✓ Game saved (${rec.offense.filter((p) => !p.np).length} PA)`);
    } catch (e) {
      showToast("Error: " + (e.message || "Could not save game"));
    } finally { setBusy(null); }
  };

  const deleteGame = async (g) => {
    setBusy(g.id);
    try { await deleteDoc(doc(gamesRef, g.id)); setConfirmDel(null); await load(); showToast("✓ Game removed"); }
    catch (e) { showToast("Error: " + (e.message || "Could not remove game")); }
    finally { setBusy(null); }
  };

  const generate = async () => {
    if (!games?.length) return;
    setBusy("gen");
    try {
      // Re-parse any game saved by an older parser version from its stored raw text.
      const records = games.map((g) => {
        if (g.parserVersion === SCOUTING_PARSER_VERSION || !g.playsText) return g;
        try { return buildGameRecord({ playsText: g.playsText, boxText: g.boxText || "", scoutedTeam: g.scoutedTeam }); } catch { return g; }
      });
      const report = buildScoutingReport(records, { opponentName: gcName, asOf });
      const lp = report.likelyPitcher;
      const data = clean({
        ...reportBase,
        generated: report,
        generatedAt: report.generatedAt,
        summary: {
          likelyPitcher: lp ? `${lp.name}${lp.num ? ` #${lp.num}` : ""}` : (existingReport?.summary?.likelyPitcher || ""),
          topThreats: report.threats.length ? report.threats : (existingReport?.summary?.topThreats || []),
          notes: existingReport?.summary?.notes || "",
        },
        uploadedBy: auth.currentUser?.email || "",
        uploadedAt: new Date().toISOString(),
      });
      await setDoc(doc(db, "gcScoutingReports", reportBase.gameId), data, { merge: true });
      onGenerated({ ...(existingReport || {}), ...data });
      showToast(`✓ Report generated from ${report.gameCount} game${report.gameCount !== 1 ? "s" : ""}`);
    } catch (e) {
      showToast("Error: " + (e.message || "Could not generate report"));
    } finally { setBusy(null); }
  };

  const label = { fontSize: "9px", color: C.textDim, letterSpacing: "2px", marginBottom: "6px" };
  const ta = { ...inputStyle, width: "100%", minHeight: "70px", resize: "vertical", fontFamily: "var(--font-family-mono, monospace)", fontSize: "10px", padding: "6px 8px" };
  const badBox = preview?.rec?.checks?.filter((c) => !c.ok) || [];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 210, background: "#000000cc", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px 16px", overflowY: "auto" }}>
      <div style={{ background: C.bg, border: `1px solid ${C.greenBorder}`, borderRadius: "6px", width: "100%", maxWidth: 720, display: "flex", flexDirection: "column", maxHeight: "92vh" }}>
        <div style={{ padding: "14px 20px", borderBottom: `2px solid ${C.gold}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: C.greenLight }}>
          <div>
            <div style={{ fontFamily: "var(--font-family-display)", fontSize: "16px", letterSpacing: "3px", color: C.gold, lineHeight: 1 }}>GC SCOUTING DATA</div>
            <div style={{ fontSize: "9px", color: C.textDim, letterSpacing: "1.5px", marginTop: "2px" }}>{gcName}{reportBase.teamName ? ` · for ${reportBase.teamName}` : ""}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: "20px", lineHeight: 1, padding: "0 4px" }}>✕</button>
        </div>

        <div style={{ overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Saved games */}
          <div>
            <div style={label}>SAVED GAMES{games ? ` · ${games.length}` : ""}{games?.length ? ` · ${win.used.length} IN LAST ${LOOKBACK_DAYS} DAYS (USED IN REPORT)` : ""}</div>
            {games === null && <div style={{ fontSize: "10px", color: C.textDim }}>Loading…</div>}
            {games?.length === 0 && <div style={{ fontSize: "10px", color: C.textDim }}>None yet. Add this opponent's recent games below.</div>}
            {games?.map((g) => {
              const bad = (g.checks || []).filter((c) => !c.ok).length;
              const pa = (g.offense || []).filter((p) => !p.np).length;
              return (
                <div key={g.id} title={outside.has(g.id) ? `Not used: outside the ${LOOKBACK_DAYS} days before this game` : undefined} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 8px", marginBottom: "4px", background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: "4px", fontSize: "10px", opacity: outside.has(g.id) ? 0.45 : 1 }}>
                  <span style={{ color: C.textMid, flexShrink: 0 }}>{g.dateLabel || g.date}</span>
                  <span style={{ color: C.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>vs {g.vs}</span>
                  {outside.has(g.id) && <span style={{ color: C.textDim, flexShrink: 0, fontStyle: "italic" }}>not used</span>}
                  <span style={{ color: C.textDim, flexShrink: 0 }}>{pa} PA</span>
                  <span title={bad ? "Some totals didn't match GC's box score" : (g.checks || []).length ? "Matches GC's box score" : "No box score pasted"} style={{ color: bad ? C.red : (g.checks || []).length ? C.teal : C.textDim, flexShrink: 0 }}>{bad ? `⚠ ${bad}` : (g.checks || []).length ? "✓ box" : "no box"}</span>
                  {confirmDel === g.id ? (
                    <>
                      <button disabled={busy === g.id} onClick={() => deleteGame(g)} style={{ ...btnGhost, padding: "2px 8px", fontSize: "9px", color: C.red, borderColor: C.red + "44" }}>DELETE</button>
                      <button onClick={() => setConfirmDel(null)} style={{ ...btnGhost, padding: "2px 8px", fontSize: "9px" }}>NO</button>
                    </>
                  ) : <button onClick={() => setConfirmDel(g.id)} title="Remove this game" style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: "12px" }}>✕</button>}
                </div>
              );
            })}
          </div>

          {/* Add a game */}
          <div style={{ padding: "12px", background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: "4px" }}>
            <div style={label}>ADD A GAME</div>
            <div style={{ fontSize: "10px", color: C.textMid, lineHeight: 1.5, marginBottom: "8px" }}>
              Signed in to GameChanger (web.gc.com), open one of <b>{gcName}</b>'s completed games. On the <b>Plays</b> tab press Ctrl+A, Ctrl+C and paste below. Then do the same on the <b>Box score</b> tab — it's used to double-check the numbers and pick up uniform numbers.
            </div>
            <div style={{ fontSize: "9px", color: C.textDim, marginBottom: "4px" }}>PLAYS PAGE</div>
            <textarea style={ta} value={playsText} onChange={(e) => setPlaysText(e.target.value)} placeholder="Paste the whole Plays page here" />
            <div style={{ fontSize: "9px", color: C.textDim, margin: "8px 0 4px" }}>BOX SCORE PAGE (recommended)</div>
            <textarea style={ta} value={boxText} onChange={(e) => setBoxText(e.target.value)} placeholder="Paste the whole Box score page here" />

            {header && teamNames.length === 2 && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "10px", color: C.textMid }}>Scouting team in this game:</span>
                <select style={{ ...inputStyle, padding: "4px 8px", fontSize: "10px", width: "auto" }} value={scouted || ""} onChange={(e) => setTeamPick(e.target.value)}>
                  {!scouted && <option value="" disabled>— pick —</option>}
                  {teamNames.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
            {preview?.error && <div style={{ marginTop: "8px", fontSize: "10px", color: C.red }}>{preview.error}</div>}
            {playsText.trim() && !header?.pas?.length && <div style={{ marginTop: "8px", fontSize: "10px", color: C.red }}>No plays found in that text — make sure it's the Plays tab.</div>}
            {preview?.rec && (() => {
              const r = preview.rec;
              const pa = r.offense.filter((p) => !p.np).length;
              const un = r.offense.filter((p) => !p.np && !p.b).length;
              const pitchers = [...new Set(r.defense.filter((p) => !p.np).map((p) => p.pi || "(unnamed)"))];
              return (
                <div style={{ marginTop: "10px", padding: "8px 10px", border: `1px solid ${badBox.length ? C.red + "66" : C.teal + "55"}`, borderRadius: "4px", fontSize: "10px", color: C.text, lineHeight: 1.6 }}>
                  <div><b>{r.dateLabel}</b> · {r.scoutedTeam} vs {r.vs}{r.date ? "" : " · (date not recognized)"}</div>
                  <div style={{ color: C.textMid }}>{pa} plate appearances{un ? ` (${un} with blank names)` : ""} · their pitchers: {pitchers.join(", ")}</div>
                  {r.checks.length === 0 && <div style={{ color: C.gold }}>No box score — numbers can't be double-checked. Pasting it is recommended.</div>}
                  {r.checks.length > 0 && badBox.length === 0 && <div style={{ color: C.teal }}>✓ All {r.checks.length} totals match GameChanger's box score</div>}
                  {badBox.map((c, i) => <div key={i} style={{ color: C.red }}>⚠ {c.what}: parsed {c.parsed}, box score {c.box ?? "not found"}</div>)}
                  {r.warnings.map((w, i) => <div key={`w${i}`} style={{ color: C.gold }}>• {w}</div>)}
                  {games?.some((g) => g.id === r.key) && <div style={{ color: C.gold }}>This game is already saved — saving replaces it.</div>}
                </div>
              );
            })()}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px", gap: "8px" }}>
              {(playsText || boxText) && <button onClick={() => { setPlaysText(""); setBoxText(""); setTeamPick(""); }} style={{ ...btnGhost, padding: "5px 12px", fontSize: "9px" }}>CLEAR</button>}
              <button disabled={!preview?.rec || busy === "save"} onClick={saveGame} style={{ ...btnPrimary, padding: "6px 14px", fontSize: "10px", opacity: !preview?.rec || busy === "save" ? 0.5 : 1, cursor: !preview?.rec ? "not-allowed" : "pointer" }}>{busy === "save" ? "SAVING…" : "SAVE GAME"}</button>
            </div>
          </div>

          {/* Generate */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", justifyContent: "space-between" }}>
            <div style={{ fontSize: "10px", color: C.textMid, flex: 1, minWidth: "200px" }}>
              {existingReport?.generated ? <>Report last generated {new Date(existingReport.generatedAt).toLocaleString()} from {existingReport.generated.gameCount} game(s). Regenerate after adding games.</> : <>Builds the report from the saved games in the {LOOKBACK_DAYS} days before this game and attaches it for coaches.</>}
            </div>
            {existingReport?.generated && <a href={`/warriors/scouting/${reportBase.gameId}`} target="_blank" rel="noopener noreferrer" style={{ ...btnGhost, padding: "6px 12px", fontSize: "10px", textDecoration: "none", color: C.teal, borderColor: C.teal + "55" }}>VIEW REPORT ↗</a>}
            <button disabled={!win.used.length || busy === "gen"} onClick={generate} style={{ ...btnPrimary, padding: "6px 14px", fontSize: "10px", opacity: !win.used.length || busy === "gen" ? 0.5 : 1, cursor: !win.used.length ? "not-allowed" : "pointer" }}>{busy === "gen" ? "GENERATING…" : existingReport?.generated ? "⚡ REGENERATE REPORT" : "⚡ GENERATE REPORT"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
